// Management shortcuts of the shared shop header (headerShopSeller.js): Dashboard | Dropshipping In |
// My Products. ONE config for both shop websites, chosen from the TYPE OF SHOP being displayed, then
// filtered by the viewer's effective-role capabilities (Rolle.js) -- never from the role alone:
//
//   seller shop   (/lakay-ShopSeller/:id[/about])      Seller workspace routes
//   provider shop (/provider-shop/:id[/about], Customer View)  Provider workspace routes
//
// These are MANAGEMENT tools: they are only built for a viewer allowed to administer THIS shop (its
// owner). A public visitor -- even one with a commercial role of their own -- never gets them, since
// every destination is the viewer's own workspace, not the displayed shop's.
import { FaTachometerAlt, FaTruckLoading, FaBoxOpen, FaHistory, FaStore } from "react-icons/fa";
import { getCommercialCapabilities, getCurrentElakayRole } from "../pageInside/Rolle";
import { getSellCenterFeatureStatus } from "../../utils/sellCenterStatus";
import { elakayAutoText as text } from "../pageInside/dataElakay";
import { PROVIDER_ROUTES } from "../dropshippingInterne/providerWorkspace";
import { DRAWER_NAV_GROUPS } from "../../components/SIdeDrawer/drawerNavConfig";
import { isAuthenticated } from "../auth";
import { SELLER_HISTORY_ROUTES } from "../dropshippingInterne/history/sellerHistoryRoutes";

const DROPSHIPPING_IN_GROUP = DRAWER_NAV_GROUPS.find((g) => g.key === "dropshippingIn").items;
const SELLER_DROPSHIPPING_IN = DROPSHIPPING_IN_GROUP.find((i) => i.key === "dropshippingIn");
// The Seller's Provider products (imported / linked from Providers): /seller/provider-products.
const SELLER_PROVIDER_PRODUCTS = DROPSHIPPING_IN_GROUP.find((i) => i.key === "myProducts");

// Real React Router destinations (Routes.js). Seller "Dashboard" = the Sell Center hub and "My
// Products" = the product admin list, both view-states of the single /myShop route (myShopNavigation.js).
// Provider "Dropshipping In": the Seller-side Dropshipping In page needs `canBrowseProviders` (combined
// roles Associate -> Family); a strict Provider cannot open it, so it gets its own Dropshipping
// Management instead (Seller and Provider stay siloed peers).
export const shopManagementNavigation = Object.freeze({
  seller: Object.freeze({
    dashboard: "/myShop?tab=sell-center",
    dropshippingIn: SELLER_DROPSHIPPING_IN.route, // /seller/dropshipping-in
    products: "/myShop?tab=products",
  }),
  provider: Object.freeze({
    dashboard: PROVIDER_ROUTES.dashboard,
    dropshippingIn: SELLER_DROPSHIPPING_IN.route,
    dropshippingManagement: PROVIDER_ROUTES.dropshipping,
    products: PROVIDER_ROUTES.products,
  }),
  // Seller visiting a Provider shop (SELLER TOOLS).
  sellerTools: Object.freeze({
    dropshippingIn: SELLER_DROPSHIPPING_IN.route, // /seller/dropshipping-in
    products: SELLER_PROVIDER_PRODUCTS.route, // /seller/provider-products
    viewedProviders: SELLER_HISTORY_ROUTES.providers, // Seller Sourcing History
    viewedProducts: SELLER_HISTORY_ROUTES.products,
  }),
});

export const SHOP_TYPES = Object.freeze(["seller", "provider"]);

// Heading of the management group (desktop tooltip area + ☰ drawer section).
export const shopManagementGroupLabel = (shopType) => (shopType === "provider"
  ? text("h_psh_group_manage", "Provider Management")
  : text("h_ssh_group_manage", "Seller Management"));

// Is the current viewer the shop's owner? `shopOwnerIds`: every identifier the shop is known by (real
// _id, URL id / publicId); `serverOwner`: an owner flag confirmed by the server when there is one.
export const isShopOwner = (infoSource, shopOwnerIds = [], serverOwner = false) => {
  if (serverOwner) return true;
  const session = isAuthenticated() || {};
  const me = infoSource?.userInfo?._id || session?.user?._id;
  if (!me) return false;
  return (Array.isArray(shopOwnerIds) ? shopOwnerIds : [shopOwnerIds]).some((id) => id && String(id) === String(me));
};

const link = (key, label, title, icon, to, status = "active") => ({ key, label, title, icon, to, inert: Boolean(status && status !== "active") });

// [{ key, label, title, icon, to, inert }] for the header and the ☰ drawer; [] when nothing applies.
export const buildShopManagementLinks = ({ shopType, infoSource, canManage }) => {
  if (!canManage || !SHOP_TYPES.includes(shopType)) return [];
  const caps = getCommercialCapabilities(infoSource);
  const role = getCurrentElakayRole(infoSource);
  const status = (key) => (key ? getSellCenterFeatureStatus(infoSource?.allSettings, key, role) : "active");
  const dashboardLabel = text("h_psh_dashboard_short", "Dashboard");
  const dropshippingInLabel = text("h_psh_dropshipping_in", "Dropshipping In");
  const productsLabel = text("h_psh_my_products", "My Products");

  if (shopType === "seller") {
    const routes = shopManagementNavigation.seller;
    if (!caps.canAccessSellerMyShop) return [];
    return [
      link("dashboard", dashboardLabel, text("h_ssh_dashboard", "Seller Dashboard"), FaTachometerAlt, routes.dashboard),
      caps.canBrowseProviders && link("dropshippingIn", dropshippingInLabel, dropshippingInLabel, FaTruckLoading, routes.dropshippingIn, status(SELLER_DROPSHIPPING_IN.animationKey)),
      link("products", productsLabel, productsLabel, FaBoxOpen, routes.products),
    ].filter(Boolean);
  }

  const routes = shopManagementNavigation.provider;
  if (!caps.canAccessProviderCenter) return [];
  return [
    link("dashboard", dashboardLabel, text("h_psh_dashboard", "Provider Dashboard"), FaTachometerAlt, routes.dashboard),
    caps.canBrowseProviders
      ? link("dropshippingIn", dropshippingInLabel, dropshippingInLabel, FaTruckLoading, routes.dropshippingIn, status(SELLER_DROPSHIPPING_IN.animationKey))
      : caps.canManageProviderDropshipping && link("dropshipping", text("h_psh_dropshipping", "Dropshipping"), text("h_pvc_nav_dropshipping", "Dropshipping Management"), FaTruckLoading, routes.dropshippingManagement, status("providerDropshipping")),
    caps.canManageProviderProducts && link("products", productsLabel, productsLabel, FaBoxOpen, routes.products, status("manageProviderProducts")),
  ].filter(Boolean);
};

// SELLER TOOLS on a Provider shop: a visitor who may use the Seller features (effective role, so
// RoleSwitchDropdown-aware: Seller and the combined roles Associate -> Family; never a strict Provider,
// Royalty or User) gets shortcuts back to ITS OWN Seller workspace while sourcing -- Seller Dropshipping
// In and the Seller's own Provider products (/seller/provider-products: what it imported / linked from
// Providers), never the visited Provider's products (/provider/products) nor the general Seller list.
export const buildSellerVisitorTools = ({ infoSource }) => {
  const caps = getCommercialCapabilities(infoSource);
  const role = getCurrentElakayRole(infoSource);
  const routes = shopManagementNavigation.sellerTools;
  const dropshippingInLabel = text("h_psh_dropshipping_in", "Dropshipping In");
  const productsLabel = text("h_psh_my_products", "My Products");
  return [
    caps.canBrowseProviders && link("dropshippingIn", dropshippingInLabel, dropshippingInLabel, FaTruckLoading, routes.dropshippingIn,
      getSellCenterFeatureStatus(infoSource?.allSettings, SELLER_DROPSHIPPING_IN.animationKey, role)),
    // Same capability as the route guard (Routes.js: UserSeller capability="canImportProviderProduct").
    caps.canImportProviderProduct && link("products", productsLabel, text("h_psh_my_provider_products", "My Provider Products"), FaBoxOpen, routes.products,
      getSellCenterFeatureStatus(infoSource?.allSettings, SELLER_PROVIDER_PRODUCTS.animationKey, role)),
  ].filter(Boolean);
};

// Seller Sourcing History shortcuts (Viewed Providers / Viewed Products): same Seller capability as
// the history API (requireSellerOwnerOrAdmin = Seller or combined role).
export const buildSellerHistoryLinks = ({ infoSource }) => {
  if (!getCommercialCapabilities(infoSource).canBrowseProviders) return [];
  const routes = shopManagementNavigation.sellerTools;
  return [
    link("viewedProviders", text("h_sph_tab_providers", "Viewed Providers"), text("h_sph_providers_page", "Provider Shops Viewed"), FaStore, routes.viewedProviders),
    link("viewedProducts", text("h_sph_tab_products", "Viewed Products"), text("h_sph_products_page", "Provider Products Viewed"), FaBoxOpen, routes.viewedProducts),
  ];
};

// The tool group of a shop header: { label, links, history? }.
//   owner of the shop            -> <TYPE> MANAGEMENT (Dashboard | Dropshipping In | My Products)
//   visitor of a Provider shop   -> SELLER TOOLS (Dropshipping In | My Products | History ▾) when Seller-capable
//   visitor of a Seller shop     -> nothing
export const buildShopHeaderTools = ({ shopType, infoSource, canManage }) => {
  if (!SHOP_TYPES.includes(shopType)) return { label: "", links: [] };
  if (canManage) return { label: shopManagementGroupLabel(shopType), links: buildShopManagementLinks({ shopType, infoSource, canManage }) };
  if (shopType === "provider") {
    return {
      label: text("h_psh_group_seller_tools", "Seller Tools"),
      links: buildSellerVisitorTools({ infoSource }),
      history: { label: text("h_sph_history", "History"), icon: FaHistory, links: buildSellerHistoryLinks({ infoSource }) },
    };
  }
  return { label: "", links: [] };
};
